/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { format } from "date-fns";
import React from "react";

import { AdminSalesDashboardHeader } from "./AdminSalesDashboardHeader";
import { AdminSalesDashboardSummary } from "./AdminSalesDashboardSummary";
import { DashboardSectionTitle } from "./DashboardSectionTitle";
import { EventSalesDetailsTable } from "./EventSalesDetailsTable";
import { TicketSalesByOrderDateDashboard } from "./TicketSalesByOrderDateDashboard";
import { minBigInt } from "./minBigInt";
import { useAdminEventParams } from "./useAdminEventParams";
import { useGetEventSalesDetails } from "./useGetEventSalesDetails";
import { useGetSalesDashboardEvents } from "./useGetSalesDashboardEvents";
import { useGetTicketSalesByOrderDate } from "./useGetTicketSalesByOrderDate";

const formatBigqueryLastWrittenAt = (
  bigqueryLastWrittenAt: bigint | undefined
): string => {
  if (bigqueryLastWrittenAt === undefined) {
    return "未取得";
  }

  return format(new Date(Number(bigqueryLastWrittenAt)), "yyyy/M/d H:mm:ss");
};

export const AdminSalesDashboard: React.FC = () => {
  const { eventIds } = useAdminEventParams();
  const { eventNames, mutate: mutateSalesDashboardEvents } =
    useGetSalesDashboardEvents(eventIds);
  const {
    fetchedData: eventSalesDetailsResponse,
    isLoading: isLoadingEventSales,
    mutate: mutateEventSalesDetails,
  } = useGetEventSalesDetails(eventIds);
  const {
    fetchedData: ticketSalesResponse,
    isLoading: isLoadingTicketSales,
    mutate: mutateTicketSalesByOrderDate,
  } = useGetTicketSalesByOrderDate(eventIds);

  const salesDetails = React.useMemo(
    () => eventSalesDetailsResponse?.eventSalesDetails ?? [],
    [eventSalesDetailsResponse]
  );
  const isSummaryLoading = isLoadingEventSales && !eventSalesDetailsResponse;

  const fetchedAt = formatBigqueryLastWrittenAt(
    minBigInt([
      eventSalesDetailsResponse?.bigqueryLastWrittenAt,
      ticketSalesResponse?.bigqueryLastWrittenAt,
    ])
  );

  const summaryCards = React.useMemo(() => {
    const { confirmedSales, salesCount } = salesDetails.reduce(
      (summary, detail) => {
        return {
          confirmedSales:
            summary.confirmedSales + detail.price * detail.quantity,
          salesCount: summary.salesCount + detail.quantity,
        };
      },
      {
        confirmedSales: 0,
        salesCount: 0,
      }
    );

    return [
      {
        label: "確定売上",
        prefix: "¥",
        value: confirmedSales.toLocaleString("ja-JP"),
      },
      {
        label: "販売数",
        value: salesCount.toLocaleString("ja-JP"),
        unit: "枚",
      },
    ];
  }, [salesDetails]);

  const handleRefreshClick = React.useCallback(() => {
    void Promise.all([
      mutateSalesDashboardEvents(),
      mutateEventSalesDetails(),
      mutateTicketSalesByOrderDate(),
    ]).catch((error) => {
      console.error("Error refreshing sales dashboard:", error);
    });
  }, [
    mutateEventSalesDetails,
    mutateSalesDashboardEvents,
    mutateTicketSalesByOrderDate,
  ]);

  return (
    <main css={styles.page}>
      <div css={styles.inner}>
        <AdminSalesDashboardHeader
          eventNames={eventNames}
          fetchedAt={fetchedAt}
          onRefreshClick={handleRefreshClick}
        />

        <section css={styles.section}>
          <DashboardSectionTitle title="サマリー" />
          <AdminSalesDashboardSummary
            summaryCards={summaryCards}
            isSummaryLoading={isSummaryLoading}
          />
        </section>

        <section css={styles.section}>
          <DashboardSectionTitle
            title="券種別"
            subText={`${salesDetails.length}券種`}
          />
          {/* 券種別テーブルパネル */}
          <EventSalesDetailsTable
            salesDetails={salesDetails}
            isSummaryLoading={isSummaryLoading}
          />
        </section>

        <TicketSalesByOrderDateDashboard
          data={ticketSalesResponse}
          isLoading={isLoadingTicketSales}
        />
      </div>
    </main>
  );
};

const styles = {
  page: css`
    width: 100%;
    min-height: 100vh;
    padding: clamp(20px, 3.5vw, 30px) clamp(16px, 4vw, 44px)
      clamp(48px, 7vw, 72px);
    background: #131c26;
  `,
  inner: css`
    display: flex;
    flex-direction: column;
    gap: clamp(18px, 3vw, 26px);
    width: 100%;
    max-width: 1360px;
    margin: 0 auto;
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: 14px;
  `,
};
